import React, { useState, useEffect, useCallback } from 'react';
import SidePanel from '../ui/SidePanel';
import Button from '../ui/Button';
import InputComponent, { Textarea, SearchInput } from '../ui/Input';
import Card, { CardHeader } from '../ui/Card';
import { StatusBadge } from '../ui/Table';
import {
  UserGroupIcon,
  EnvelopeIcon,
  CogIcon,
  ShieldCheckIcon,
  UsersIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import {
  UserGroupIcon as UserGroupIconSolid
} from '@heroicons/react/24/solid';
import axiosInstance from '../api/axiosInstance';
import logger from '../../utils/logger';

const EditTeamSidePanel = ({ 
  isOpen, 
  onClose, 
  team, 
  onSubmit, 
  loading = false,
  showNotification
}) => {

  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'team',
    privacy: 'private',
    settings: {
      allowMessaging: true,
      allowFileSharing: true,
      allowMemberInvites: false,
      autoAddNewUsers: false,
      requireApproval: true,
      allowGuestAccess: false
    }
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Member management state
  const [members, setMembers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectedRole, setSelectedRole] = useState('member');
  const [showAddSection, setShowAddSection] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Initialize form data when team changes
  useEffect(() => {
    if (team) {

      setFormData({
        name: team.name || '',
        description: team.description || '',
        type: team.type || 'team',
        privacy: team.privacy || 'private',
        settings: {
          allowMessaging: team.settings?.allowMessaging ?? true,
          allowFileSharing: team.settings?.allowFileSharing ?? true,
          allowMemberInvites: team.settings?.allowMemberInvites ?? false,
          autoAddNewUsers: team.settings?.autoAddNewUsers ?? false,
          requireApproval: team.settings?.requireApproval ?? true,
          allowGuestAccess: team.settings?.allowGuestAccess ?? false
        }
      });
      setMembers(team.members || []);
    }
  }, [team]);

  // Fetch available users for adding members
  const fetchAvailableUsers = useCallback(async () => {
    if (!team?._id) return;

    try {
      setLoadingMembers(true);
      const response = await axiosInstance.get(`/api/business/users`);

      if (response.data.success) {
        // Filter out users who are already members - use a function to get latest members
        setAvailableUsers(response.data.users);
      }
    } catch (error) {
      logger.error('Error fetching available users:', error);
      showNotification?.('Failed to load available users', 'error');
    } finally {
      setLoadingMembers(false);
    }
  }, [team?._id, showNotification]);

  // Load available users when members tab is active
  useEffect(() => {
    if (activeTab === 'members' && isOpen) {
      fetchAvailableUsers();
    }
  }, [activeTab, isOpen, fetchAvailableUsers]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleSettingChange = (setting, value) => {
    setFormData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [setting]: value
      }
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Team name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Team name must be at least 2 characters';
    }
    
    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      const success = await onSubmit(formData);
      if (success) {
        showNotification && showNotification('Team updated successfully', 'success');
        onClose();
      }
    } catch (error) {
      showNotification && showNotification('Failed to update team', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Member management handlers
  const handleToggleUser = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleAddMembers = async () => {
    if (selectedUsers.size === 0) return;

    try {
      setSaving(true);
      const userIds = Array.from(selectedUsers);

      const response = await axiosInstance.post(`/api/business/teams/${team._id}/members`, {
        userIds,
        role: selectedRole
      });

      if (response.data.success) {
        showNotification?.('Members added successfully', 'success');
        setMembers(response.data.team.members);
        setSelectedUsers(new Set());
        setShowAddSection(false);
        fetchAvailableUsers();
      }
    } catch (error) {
      logger.error('Error adding members:', error);
      showNotification?.('Failed to add members', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;

    try {
      setSaving(true);
      const response = await axiosInstance.delete(
        `/api/business/teams/${team._id}/members/${memberId}`
      );

      if (response.data.success) {
        showNotification?.('Member removed successfully', 'success');
        setMembers(response.data.team.members);
        fetchAvailableUsers();
      }
    } catch (error) {
      logger.error('Error removing member:', error);
      showNotification?.('Failed to remove member', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMemberRole = async (memberId, newRole) => {
    try {
      setSaving(true);
      const response = await axiosInstance.patch(
        `/api/business/teams/${team._id}/members/${memberId}`,
        { role: newRole }
      );

      if (response.data.success) {
        showNotification?.('Member role updated successfully', 'success');
        setMembers(response.data.team.members);
      }
    } catch (error) {
      logger.error('Error updating member role:', error);
      showNotification?.('Failed to update member role', 'error');
    } finally {
      setSaving(false);
    }
  };

  const filteredAvailableUsers = availableUsers.filter(user => {
    // Exclude users who are already members
    const memberIds = new Set(members.map(m => m.userId?._id || m.userId));
    if (memberIds.has(user._id)) {
      return false;
    }

    // Apply search filter
    const searchLower = searchQuery.toLowerCase();
    return (
      user.firstName?.toLowerCase().includes(searchLower) ||
      user.lastName?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.department?.toLowerCase().includes(searchLower)
    );
  });

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const tabs = [
    {
      id: 'general',
      label: 'General',
      icon: <InformationCircleIcon className="w-4 h-4" />,
      description: 'Basic team information'
    },
    {
      id: 'members',
      label: 'Members',
      icon: <UsersIcon className="w-4 h-4" />,
      description: 'Manage team members'
    },
    {
      id: 'permissions',
      label: 'Permissions',
      icon: <ShieldCheckIcon className="w-4 h-4" />,
      description: 'Team access controls'
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <CogIcon className="w-4 h-4" />,
      description: 'Team preferences'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <InformationCircleIcon className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-900">Team Information</h3>
                </div>
              </CardHeader>
              
              <div className="space-y-4">
                <InputComponent
                  label="Team Name"
                  placeholder="Enter team name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  error={errors.name}
                  required
                />
                
                <Textarea
                  label="Description"
                  placeholder="Brief description of the team's purpose"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  error={errors.description}
                  rows={3}
                />

                {(formData.type === 'distribution_list' || formData.type === 'distribution') && (
                  <InputComponent
                    label="Email Alias"
                    placeholder="e.g., sales@yourdomain.com"
                    value={team?.email || ''}
                    disabled
                    hint="Email alias cannot be changed after creation"
                    icon={<EnvelopeIcon />}
                  />
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Team Type
                  </label>
                  <div className="flex space-x-3">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="type"
                        value="team"
                        checked={formData.type === 'team'}
                        onChange={(e) => handleInputChange('type', e.target.value)}
                        className="w-4 h-4 text-orange-600 border-gray-300 dark:border-gray-700 focus:ring-orange-500 dark:bg-[#2a2a2a]"
                      />
                      <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">Team</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="type"
                        value="distribution_list"
                        checked={formData.type === 'distribution_list'}
                        onChange={(e) => handleInputChange('type', e.target.value)}
                        className="w-4 h-4 text-orange-600 border-gray-300 dark:border-gray-700 focus:ring-orange-500 dark:bg-[#2a2a2a]"
                      />
                      <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">Distribution List</span>
                    </label>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Privacy Level
                  </label>
                  <div className="flex space-x-3">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="privacy"
                        value="private"
                        checked={formData.privacy === 'private'}
                        onChange={(e) => handleInputChange('privacy', e.target.value)}
                        className="w-4 h-4 text-orange-600 border-gray-300 dark:border-gray-700 focus:ring-orange-500 dark:bg-[#2a2a2a]"
                      />
                      <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">Private</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="privacy"
                        value="public"
                        checked={formData.privacy === 'public'}
                        onChange={(e) => handleInputChange('privacy', e.target.value)}
                        className="w-4 h-4 text-orange-600 border-gray-300 dark:border-gray-700 focus:ring-orange-500 dark:bg-[#2a2a2a]"
                      />
                      <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">Public</span>
                    </label>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        );

      case 'members':
        return (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-4">
              <Card padding="sm" className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 border-blue-200 dark:border-blue-800">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                    <UsersIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Current Members</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{members.length}</p>
                  </div>
                </div>
              </Card>

              <Card padding="sm" className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 border-green-200 dark:border-green-800">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                    <UserGroupIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Available Users</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{availableUsers.length}</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Current Members List */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <UsersIcon className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-900">Current Members</h3>
                  </div>
                </div>
              </CardHeader>

              <div className="divide-y divide-gray-200 dark:divide-gray-800">
                {members.length === 0 ? (
                  <div className="text-center py-8">
                    <UsersIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 dark:text-gray-400">No members yet</p>
                    <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">Add members to get started</p>
                  </div>
                ) : (
                  members.map((member) => {
                    // Handle both direct member properties and nested userId structure
                    const user = member.userId || member;
                    const firstName = user?.firstName || member?.firstName || '';
                    const lastName = user?.lastName || member?.lastName || '';
                    const email = user?.email || member?.email || '';
                    const department = user?.department || member?.department || '';

                    return (
                      <div key={member._id} className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors">
                        <div className="flex items-center space-x-3 flex-1">
                          <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white font-semibold">
                            {getInitials(firstName, lastName)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {`${firstName} ${lastName}`.trim() || 'Unnamed User'}
                              </p>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                member.role === 'admin'
                                  ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                                  : 'bg-gray-100 text-gray-700 dark:bg-[#222222] dark:text-gray-300'
                              }`}>
                                {member.role || 'member'}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{email}</p>
                            {department && (
                              <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 text-xs rounded-md">
                                {department}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          <select
                            value={member.role}
                            onChange={(e) => handleUpdateMemberRole(member._id, e.target.value)}
                            className="text-xs border border-gray-300 dark:border-gray-800 rounded-md px-2 py-1 focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:bg-[#2a2a2a] dark:text-gray-100"
                          >
                            <option value="member">Member</option>
                            <option value="admin">Admin</option>
                          </select>
                          <button
                            onClick={() => handleRemoveMember(member._id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            title="Remove member"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </Card>

            {/* Add Members Section */}
            <Card>
              <button
                onClick={() => setShowAddSection(!showAddSection)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <PlusIcon className="w-5 h-5 text-orange-600" />
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">Add Members</h3>
                </div>
                {showAddSection ? (
                  <ChevronUpIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                )}
              </button>

              {showAddSection && (
                <div className="border-t border-gray-200 dark:border-gray-800 p-4 space-y-4">
                  {/* Search and Role Selection */}
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <SearchInput
                        placeholder="Search users by name, email, or department..."
                        onSearch={setSearchQuery}
                        size="sm"
                      />
                    </div>
                    <div className="w-40">
                      <select
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-800 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:bg-[#2a2a2a] dark:text-gray-100"
                      >
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  </div>

                  {/* Available Users List */}
                  {loadingMembers ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Loading users...</p>
                    </div>
                  ) : filteredAvailableUsers.length === 0 ? (
                    <div className="text-center py-8">
                      <UserGroupIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 dark:text-gray-400">
                        {searchQuery ? 'No users found matching your search' : 'No available users'}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-800 rounded-lg divide-y divide-gray-200 dark:divide-gray-800">
                        {filteredAvailableUsers.map((user) => (
                          <label
                            key={user._id}
                            className={`flex items-center p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors ${
                              selectedUsers.has(user._id) ? 'bg-orange-50 dark:bg-orange-900/20' : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedUsers.has(user._id)}
                              onChange={() => handleToggleUser(user._id)}
                              className="w-4 h-4 text-orange-600 border-gray-300 dark:border-gray-700 rounded focus:ring-orange-500 dark:bg-[#2a2a2a]"
                            />
                            <div className="flex items-center space-x-3 ml-3 flex-1">
                              <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                {getInitials(user.firstName, user.lastName)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {user.firstName} {user.lastName}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                              </div>
                              {user.department && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 text-xs rounded-md">
                                  {user.department}
                                </span>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>

                      {/* Add Selected Button */}
                      <div className="flex items-center justify-between pt-2">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {selectedUsers.size} user{selectedUsers.size !== 1 ? 's' : ''} selected
                        </p>
                        <Button
                          variant="primary"
                          onClick={handleAddMembers}
                          disabled={selectedUsers.size === 0 || saving}
                          loading={saving}
                          icon={<PlusIcon />}
                        >
                          Add Selected Members
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </Card>
          </div>
        );

      case 'permissions':
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <ShieldCheckIcon className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-gray-900">Access Controls</h3>
                </div>
              </CardHeader>
              
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-800">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">Allow Messaging</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Members can send messages in team channels</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.settings.allowMessaging}
                        onChange={(e) => handleSettingChange('allowMessaging', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-[#222222] peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-gray-700 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-800">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">Allow File Sharing</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Members can upload and share files</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.settings.allowFileSharing}
                        onChange={(e) => handleSettingChange('allowFileSharing', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-[#222222] peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-gray-700 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-800">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">Member Invites</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Allow members to invite others to the team</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.settings.allowMemberInvites}
                        onChange={(e) => handleSettingChange('allowMemberInvites', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-[#222222] peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-gray-700 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between py-3">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">Guest Access</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Allow external users to access team resources</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.settings.allowGuestAccess}
                        onChange={(e) => handleSettingChange('allowGuestAccess', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-[#222222] peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-gray-700 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        );

      case 'settings':
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <CogIcon className="w-5 h-5 text-gray-600" />
                  <h3 className="font-semibold text-gray-900">Team Settings</h3>
                </div>
              </CardHeader>
              
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-800">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">Auto-add New Users</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Automatically add new organization members to this team</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.settings.autoAddNewUsers}
                        onChange={(e) => handleSettingChange('autoAddNewUsers', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-[#222222] peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-gray-700 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between py-3">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">Require Approval</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Require admin approval for new member requests</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.settings.requireApproval}
                        onChange={(e) => handleSettingChange('requireApproval', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-[#222222] peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-gray-700 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            </Card>

            {/* Danger Zone */}
            <Card className="border-red-200 dark:border-red-900">
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <XMarkIcon className="w-5 h-5 text-red-600" />
                  <h3 className="font-semibold text-red-900 dark:text-red-400">Danger Zone</h3>
                </div>
              </CardHeader>

              <div className="space-y-4">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <XMarkIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-medium text-red-900 dark:text-red-400">Delete Team</h4>
                      <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                        Permanently delete this team and all its data. This action cannot be undone.
                      </p>
                      <Button
                        variant="danger"
                        size="sm"
                        className="mt-3"
                        onClick={() => {
                          if (window.confirm('Are you sure you want to delete this team? This action cannot be undone.')) {
                            // Handle team deletion
                          }
                        }}
                      >
                        Delete Team
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
            {formData.type === 'distribution_list' ? (
              <EnvelopeIcon className="w-4 h-4 text-white" />
            ) : (
              <UserGroupIconSolid className="w-4 h-4 text-white" />
            )}
          </div>
          <div>
            <div className="font-semibold text-gray-900 dark:text-gray-100">
              {team?.name || 'Edit Team'}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              {formData.type === 'distribution_list' ? 'Distribution List' : 'Team'}
            </div>
          </div>
        </div>
      }
      size="4xl"
    >
      <div className="flex flex-col h-full">
        {/* Tab Navigation */}
        <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#111111]">
          <div className="flex space-x-0">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600 bg-white dark:bg-[#1a1a1a]'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6 dark:bg-[#1a1a1a]">
          {renderTabContent()}
        </div>

        {/* Footer Actions */}
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#111111] px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {team?.createdAt && (
                <span>Created {new Date(team.createdAt).toLocaleDateString()}</span>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                loading={saving}
                disabled={saving}
                icon={<CheckCircleIcon />}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </SidePanel>
  );
};

export default EditTeamSidePanel;